import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  RefreshOutlined,
  CheckCircleOutline,
  CancelOutlined,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import { tokens } from "../../theme";
import { formatKES } from "../WalletManagement/WalletBalance";
import { companyManagementUrl, walletGraphqlUrl } from "../../config";
import {
  fetchJsonDynamic,
  executeAnyMutation,
  parseErrorMessage,
} from "../../data/DynamicServiceNew/jsondynamicservice";
import {
  walletMutate,
  REQUEST_APPROVAL_OTP_MUTATION,
} from "../../data/WalletService/walletGraphQL";
import {
  loadUsersMap,
  getUserDisplayName,
  getMaskedUserEmail,
  resolveUnknownUsers,
} from "./userResolver";

const ACTION_APPROVE = 1;
const ACTION_REJECT = 2;

const parseRows = (res) => {
  if (!res?.results) return [];
  try {
    const parsed =
      typeof res.results === "string" ? JSON.parse(res.results) : res.results;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const formatDateTime = (val) => {
  if (!val) return "—";
  try {
    const d = new Date(val);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
  } catch {
    return "—";
  }
};

const WithdrawalApprovals = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [rows, setRows] = useState([]);
  const [approvedRows, setApprovedRows] = useState([]);
  const [companyNames, setCompanyNames] = useState({});
  const [userMap, setUserMap] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [approvedLoading, setApprovedLoading] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [approvedPageSize, setApprovedPageSize] = useState(10);

  useEffect(() => {
    let mounted = true;
    loadUsersMap().then((map) => {
      if (mounted && map) setUserMap(new Map(map));
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const allRows = [...rows, ...approvedRows];
    if (allRows.length === 0) return;
    let mounted = true;

    const idsToCheck = new Set();
    allRows.forEach((r) => {
      [r.createdby, r.requested_by, r.updatedby, r.approved_by, r.approvedby]
        .filter(Boolean)
        .forEach((id) => idsToCheck.add(Number(id)));
    });

    const unknownIds = [...idsToCheck].filter((id) => {
      const name = getUserDisplayName(id, userMap);
      return name.startsWith("User #");
    });

    if (unknownIds.length === 0) return;

    resolveUnknownUsers(unknownIds).then((updatedMap) => {
      if (mounted) setUserMap(new Map(updatedMap));
    });

    return () => {
      mounted = false;
    };
  }, [rows, approvedRows]);

  const loadCompanyNames = useCallback(async () => {
    try {
      const res = await fetchJsonDynamic(
        "companies",
        null,
        null,
        companyManagementUrl.uri
      );
      const map = {};
      parseRows(res).forEach((c) => {
        const id = c.Id ?? c.id;
        map[id] = c.CompanyName ?? c.companyName ?? c.Name ?? c.name;
      });
      setCompanyNames(map);
    } catch (err) {
      console.warn("Could not load company names:", err);
    }
  }, []);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchJsonDynamic(
        "walletWithdrawal",
        "wallet",
        { iscancelled: 0, isapproved: 0 },
        walletGraphqlUrl.uri
      );
      if (res?.error) {
        throw new Error(parseErrorMessage(res.error, "Withdrawal"));
      }
      setRows(parseRows(res));
    } catch (err) {
      console.error("Failed to load withdrawal requests:", err);
      setRows([]);
      Swal.fire({
        icon: "error",
        title: "Could not load requests",
        text: err.message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadApprovedRequests = useCallback(async () => {
    setApprovedLoading(true);
    try {
      const [resApproved, resCancelled] = await Promise.all([
        fetchJsonDynamic("walletWithdrawal", "wallet", { isapproved: 1 }, walletGraphqlUrl.uri),
        fetchJsonDynamic("walletWithdrawal", "wallet", { iscancelled: 1 }, walletGraphqlUrl.uri),
      ]);
      const approved = parseRows(resApproved);
      const cancelled = parseRows(resCancelled);
      const seenIds = new Set();
      const merged = [...approved, ...cancelled].filter((r) => {
        if (seenIds.has(r.id)) return false;
        seenIds.add(r.id);
        return true;
      });
      merged.sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));
      setApprovedRows(merged);
    } catch (err) {
      console.error("Failed to load approved withdrawal requests:", err);
      setApprovedRows([]);
    } finally {
      setApprovedLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanyNames();
    loadRequests();
    loadApprovedRequests();
  }, [loadCompanyNames, loadRequests, loadApprovedRequests]);

  const submitAction = async (row, action, narration, otp = "") => {
    setActingId(row.id);
    try {
      const response = await executeAnyMutation(
        "insertWalletWitdrawal",
        {
          p_phone_number: String(row.phone_number ?? ""),
          p_amount: Number(row.amount),
          p_narration: narration,
          p_withdrawal_id: Number(row.id),
          p_action: action,
          p_otp: otp || undefined,
        },
        "wallet",
        false,
        walletGraphqlUrl.uri
      );
      if (!response || response.error) {
        throw new Error(parseErrorMessage(response?.error, "Withdrawal"));
      }
      await Swal.fire({
        icon: "success",
        title: action === ACTION_APPROVE ? "Withdrawal approved" : "Withdrawal rejected",
        timer: 2000,
        showConfirmButton: false,
      });
      await Promise.all([loadRequests(), loadApprovedRequests()]);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Action failed", text: err.message });
    } finally {
      setActingId(null);
    }
  };

  const handleApprove = async (row) => {
    let currentUserId = 0;
    let canApprove = false;
    try {
      const decoded = JSON.parse(localStorage.getItem("decodedToken") || "{}");
      currentUserId = Number(decoded.Id || decoded.UserId || decoded.id || 0);
      const isSuper = Number(decoded.RoleGroupId || 0) === 1 || (decoded.RoleName || "").toLowerCase().includes("super");
      const userRights = decoded.Rights || decoded.UserRights || decoded.rights || [];
      const hasSpecificRight = Array.isArray(userRights) && userRights.some((r) => {
        const name = (typeof r === "string" ? r : r.RightName || r.name || "").toLowerCase();
        return name.includes("withdrawal") && (name.includes("approv") || name.includes("check"));
      });
      canApprove = isSuper || hasSpecificRight;
    } catch (e) {
      console.warn("Could not read decodedToken for checker validation:", e);
      canApprove = false;
    }

    if (!canApprove) {
      await Swal.fire({
        icon: "error",
        title: "Not Authorized",
        text: "You do not have SGW approval rights to approve withdrawals. Contact your system administrator.",
        confirmButtonColor: "#d32f2f",
      });
      return;
    }

    const requesterId = Number(row.createdby ?? row.requested_by ?? 0);
    if (requesterId > 0 && currentUserId > 0 && requesterId === currentUserId) {
      await Swal.fire({
        icon: "error",
        title: "Maker-Checker Violation",
        text: "Dual control policy violation: You cannot approve a withdrawal request that you created yourself. Another authorized checker must perform this approval.",
        confirmButtonColor: "#d32f2f",
      });
      return;
    }

    const confirmResult = await Swal.fire({
      icon: "question",
      title: "Approve withdrawal?",
      html: `
        <div style="text-align: left; font-size: 0.9rem; line-height: 1.6;">
          <p><strong>Request:</strong> #${row.id}</p>
          <p><strong>Company:</strong> ${companyNames[row.company_id] || `Tenant #${row.company_id}`}</p>
          <p><strong>Phone:</strong> ${row.phone_number}</p>
          <p><strong>Amount:</strong> ${formatKES(row.amount)}</p>
          <p><strong>Fee:</strong> ${formatKES(row.fee)}</p>
          <p><strong>Total Debit:</strong> ${formatKES(row.total_debit)}</p>
          <hr style="border: 0; border-top: 1px solid #ddd; margin: 10px 0;" />
          <p style="color: #666; font-size: 0.8rem;">
            An approval OTP will be sent to your registered email address to verify this authorization.
          </p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Send OTP & Continue",
      confirmButtonColor: "#22bf72",
      cancelButtonText: "Cancel",
    });

    if (!confirmResult.isConfirmed) return;

    let maskedEmail = "";
    try {
      Swal.fire({
        title: "Requesting OTP...",
        text: "Please wait while we generate your verification code.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const otpGqlRes = await walletMutate(REQUEST_APPROVAL_OTP_MUTATION, {
        actionType: "WITHDRAWAL_APPROVAL",
        entityId: Number(row.id),
      });

      if (!otpGqlRes || otpGqlRes.errors) {
        throw new Error(
          otpGqlRes?.errors?.[0]?.message || "Failed to trigger approval OTP"
        );
      }
      maskedEmail = getMaskedUserEmail(currentUserId, userMap);
    } catch (otpErr) {
      Swal.fire({
        icon: "error",
        title: "Could not send OTP",
        text: otpErr.message || "Failed to dispatch approval OTP to your email.",
      });
      return;
    }

    let timerInterval = null;
    let secondsLeft = 30;

    const otpResult = await Swal.fire({
      icon: "info",
      title: "Enter Approval OTP",
      html: `
        <p style="font-size: 0.88rem; color: #555; margin-bottom: 8px;">
          A 6-digit verification code has been dispatched to
          <strong>${maskedEmail || "your registered email"}</strong>.
        </p>
        <input
          id="swal-approval-otp"
          class="swal2-input"
          placeholder="Enter 6-digit OTP"
          maxlength="6"
          style="text-align: center; letter-spacing: 4px; font-weight: 700; font-size: 1.2rem; width: 220px;"
          autocomplete="one-time-code"
          inputmode="numeric"
        />
        <div style="margin-top: 10px; font-size: 0.8rem; color: #888;">
          <span id="swal-otp-timer">Resend available in ${secondsLeft}s</span>
          <br/>
          <button
            type="button"
            id="swal-otp-resend-btn"
            disabled
            style="
              margin-top: 6px;
              background: none;
              border: none;
              color: #1766a2;
              text-decoration: underline;
              cursor: not-allowed;
              opacity: 0.5;
              font-size: 0.8rem;
            "
          >
            Resend OTP
          </button>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Verify & Approve",
      confirmButtonColor: "#22bf72",
      cancelButtonText: "Cancel",
      didOpen: () => {
        const input = document.getElementById("swal-approval-otp");
        if (input) input.focus();

        const timerSpan = document.getElementById("swal-otp-timer");
        const resendBtn = document.getElementById("swal-otp-resend-btn");

        timerInterval = setInterval(() => {
          secondsLeft -= 1;
          if (secondsLeft > 0) {
            if (timerSpan) timerSpan.innerText = `Resend available in ${secondsLeft}s`;
          } else {
            clearInterval(timerInterval);
            if (timerSpan) timerSpan.innerText = "Didn't receive code?";
            if (resendBtn) {
              resendBtn.disabled = false;
              resendBtn.style.cursor = "pointer";
              resendBtn.style.opacity = "1";
            }
          }
        }, 1000);

        if (resendBtn) {
          resendBtn.onclick = async () => {
            resendBtn.disabled = true;
            resendBtn.style.cursor = "not-allowed";
            resendBtn.style.opacity = "0.5";
            if (timerSpan) timerSpan.innerText = "Requesting new code...";
            try {
              await walletMutate(REQUEST_APPROVAL_OTP_MUTATION, {
                actionType: "WITHDRAWAL_APPROVAL",
                entityId: Number(row.id),
              });
              secondsLeft = 30;
              if (timerSpan) timerSpan.innerText = `New code sent! Resend in ${secondsLeft}s`;
              timerInterval = setInterval(() => {
                secondsLeft -= 1;
                if (secondsLeft > 0) {
                  if (timerSpan) timerSpan.innerText = `Resend available in ${secondsLeft}s`;
                } else {
                  clearInterval(timerInterval);
                  if (timerSpan) timerSpan.innerText = "Didn't receive code?";
                  resendBtn.disabled = false;
                  resendBtn.style.cursor = "pointer";
                  resendBtn.style.opacity = "1";
                }
              }, 1000);
            } catch (rErr) {
              if (timerSpan) timerSpan.innerText = "Failed to resend code.";
              resendBtn.disabled = false;
              resendBtn.style.cursor = "pointer";
              resendBtn.style.opacity = "1";
            }
          };
        }
      },
      willClose: () => {
        if (timerInterval) clearInterval(timerInterval);
      },
      preConfirm: () => {
        const input = document.getElementById("swal-approval-otp");
        const val = input ? input.value.trim() : "";
        if (!val) {
          Swal.showValidationMessage("Please enter the 6-digit verification code");
          return false;
        }
        if (!/^\d{6}$/.test(val)) {
          Swal.showValidationMessage("Verification code must be exactly 6 digits");
          return false;
        }
        return val;
      },
    });

    if (!otpResult.isConfirmed || !otpResult.value) return;

    await submitAction(row, ACTION_APPROVE, row.narration || "Approved", otpResult.value);
  };

  const handleReject = async (row) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Reject withdrawal?",
      input: "text",
      inputLabel: "Reason for rejection",
      inputValidator: (v) => (!v?.trim() ? "A reason is required" : null),
      showCancelButton: true,
      confirmButtonText: "Reject",
      confirmButtonColor: "#d33",
    });
    if (!result.isConfirmed) return;
    await submitAction(row, ACTION_REJECT, result.value.trim());
  };

  const pendingColumns = useMemo(
    () => [
      {
        field: "id",
        headerName: "Request",
        width: 100,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            #{params.value}
          </Typography>
        ),
      },
      {
        field: "company_id",
        headerName: "Company",
        width: 180,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {companyNames[params.value] || `Tenant #${String(params.value).padStart(4, "0")}`}
          </Typography>
        ),
      },
      {
        field: "phone_number",
        headerName: "Phone",
        width: 130,
      },
      {
        field: "amount",
        headerName: "Amount",
        width: 130,
        type: "number",
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#22bf72" }}>
            {formatKES(params.value)}
          </Typography>
        ),
      },
      {
        field: "fee",
        headerName: "Fee",
        width: 110,
        type: "number",
        align: "right",
        headerAlign: "right",
        renderCell: (params) => formatKES(params.value),
      },
      {
        field: "total_debit",
        headerName: "Total Debit",
        width: 130,
        type: "number",
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {formatKES(params.value)}
          </Typography>
        ),
      },
      {
        field: "balance_before",
        headerName: "Balance Before",
        width: 140,
        type: "number",
        align: "right",
        headerAlign: "right",
        renderCell: (params) => formatKES(params.value),
      },
      {
        field: "narration",
        headerName: "Narration",
        width: 180,
        renderCell: (params) => params.value || "—",
      },
      {
        field: "requested",
        headerName: "Requested",
        width: 200,
        valueGetter: (params) => params.row.datecreated,
        renderCell: (params) => {
          const row = params.row;
          return (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700, color: "#8b5cf6" }}>
                {getUserDisplayName(row.createdby ?? row.requested_by, userMap)}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                {formatDateTime(row.datecreated)}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 220,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const row = params.row;
          const busy = actingId === row.id;
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="contained"
                disabled={busy}
                startIcon={
                  busy ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutline />
                }
                onClick={() => handleApprove(row)}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#22bf72",
                  "&:hover": { backgroundColor: "#1b995b" },
                  fontSize: "0.75rem",
                  py: 0.5,
                }}
              >
                Approve
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={busy}
                startIcon={<CancelOutlined />}
                onClick={() => handleReject(row)}
                sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.5 }}
              >
                Reject
              </Button>
            </Stack>
          );
        },
      },
    ],
    [companyNames, userMap, colors, actingId]
  );

  const historyColumns = useMemo(
    () => [
      {
        field: "id",
        headerName: "#",
        width: 90,
      },
      {
        field: "company_id",
        headerName: "Company",
        width: 180,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {companyNames[params.value] || `Tenant #${String(params.value).padStart(4, "0")}`}
          </Typography>
        ),
      },
      {
        field: "phone_number",
        headerName: "Phone",
        width: 130,
      },
      {
        field: "amount",
        headerName: "Amount",
        width: 130,
        type: "number",
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#22bf72" }}>
            {formatKES(params.value)}
          </Typography>
        ),
      },
      {
        field: "fee",
        headerName: "Fee",
        width: 110,
        type: "number",
        align: "right",
        headerAlign: "right",
        renderCell: (params) => formatKES(params.value),
      },
      {
        field: "total_debit",
        headerName: "Total Debit",
        width: 130,
        type: "number",
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {formatKES(params.value)}
          </Typography>
        ),
      },
      {
        field: "balance_before",
        headerName: "Bal. Before",
        width: 140,
        type: "number",
        align: "right",
        headerAlign: "right",
        renderCell: (params) => formatKES(params.value),
      },
      {
        field: "narration",
        headerName: "Narration",
        width: 180,
        renderCell: (params) => params.value || "—",
      },
      {
        field: "requested_by",
        headerName: "Requested By",
        width: 160,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#8b5cf6" }}>
            {getUserDisplayName(params.row.createdby ?? params.row.requested_by, userMap)}
          </Typography>
        ),
      },
      {
        field: "datecreated",
        headerName: "Requested Time",
        width: 170,
        renderCell: (params) => (
          <Typography variant="caption" sx={{ color: colors.grey[400] }}>
            {formatDateTime(params.value)}
          </Typography>
        ),
      },
      {
        field: "approved_by",
        headerName: "Approver",
        width: 160,
        renderCell: (params) => {
          const row = params.row;
          const approverId = row.updatedby ?? row.approved_by ?? row.approvedby;
          return (
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#22bf72" }}>
              {getUserDisplayName(approverId, userMap)}
            </Typography>
          );
        },
      },
      {
        field: "dateupdated",
        headerName: "Approval Time",
        width: 170,
        renderCell: (params) => (
          <Typography variant="caption" sx={{ color: colors.grey[400] }}>
            {formatDateTime(params.row.dateupdated || params.row.datecreated)}
          </Typography>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        renderCell: (params) => {
          const row = params.row;
          const isApproved = Boolean(row.isapproved);
          return (
            <Chip
              label={isApproved ? "Approved" : "Rejected"}
              color={isApproved ? "success" : "error"}
              size="small"
              sx={{ fontWeight: 700 }}
            />
          );
        },
      },
    ],
    [companyNames, userMap, colors]
  );

  const gridSx = {
    border: "none",
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: isDark ? colors.primary[500] : "#f1f5f9",
      fontWeight: 700,
      borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#cbd5e1"}`,
    },
    "& .MuiDataGrid-cell": {
      borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.04)" : "#f1f5f9"}`,
    },
    "& .MuiDataGrid-virtualScroller": {
      "&::-webkit-scrollbar": {
        height: "12px",
        width: "12px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0",
        borderRadius: "6px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: isDark ? "rgba(255,255,255,0.25)" : "#94a3b8",
        borderRadius: "6px",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        backgroundColor: isDark ? "rgba(255,255,255,0.35)" : "#64748b",
      },
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
    },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3.5, width: "100%", maxWidth: "100%", minWidth: 0, overflowX: "hidden" }}>
      {/* ── Pending Withdrawal Approvals Card ──────────────────────── */}
      <Card
        sx={{
          borderRadius: 2.5,
          backgroundColor: isDark ? colors.primary[400] : "#ffffff",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 3, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                Pending Withdrawal Approvals
              </Typography>
              <Chip
                label={`${rows.length} Pending`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 700, fontSize: "0.75rem", height: 24 }}
              />
            </Box>
            <Tooltip title="Refresh pending requests">
              <IconButton size="small" onClick={loadRequests} disabled={loading}>
                {loading ? <CircularProgress size={18} /> : <RefreshOutlined sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                No withdrawal requests are awaiting approval.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ width: "100%" }}>
              <DataGrid
                autoHeight
                rows={rows}
                columns={pendingColumns}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[5, 10, 25, 50]}
                disableSelectionOnClick
                sx={gridSx}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Approved / Rejected Withdrawals Card ──────────────────── */}
      <Card
        sx={{
          borderRadius: 2.5,
          backgroundColor: isDark ? colors.primary[400] : "#ffffff",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 3, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                Approved / Rejected Withdrawals
              </Typography>
              <Chip
                label={`${approvedRows.length} Record${approvedRows.length !== 1 ? "s" : ""}`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 700, fontSize: "0.75rem", height: 24 }}
              />
            </Box>
            <Tooltip title="Refresh approved withdrawals">
              <IconButton size="small" onClick={loadApprovedRequests} disabled={approvedLoading}>
                {approvedLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  <RefreshOutlined sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>

          {approvedLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : approvedRows.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                No approved or rejected withdrawal records found.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ width: "100%" }}>
              <DataGrid
                autoHeight
                rows={approvedRows}
                columns={historyColumns}
                pageSize={approvedPageSize}
                onPageSizeChange={(newPageSize) => setApprovedPageSize(newPageSize)}
                rowsPerPageOptions={[5, 10, 25, 50]}
                disableSelectionOnClick
                sx={gridSx}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default WithdrawalApprovals;
